import { Observable, Subject } from 'rxjs';

import { OSNotification, WlWindow } from 'typings/globals/cordova';
import { PushMessage } from 'src/services/PushMessageHandler';
import Analytics from '@withlocals/withlocals-analytics';
import useAuthStore from '~/authentication/store';

declare const window: WlWindow;

interface OneSignalNotificationEvent {
  complete?: any;
  action?: any;
  notification: OSNotification;
}

export interface OneSignalService {
  notificationOpens$: Observable<OneSignalNotificationEvent>;
  notificationReceives$: Observable<OneSignalNotificationEvent>;
  notificationOpensData$: Observable<PushMessage>;
  notificationReceivesData$: Observable<PushMessage>;
  requestPushPermissions: (autoContinueToSettings?: boolean) => void;
}

export function OneSignal(): OneSignalService {
  console.debug('Starting service for OneSignal with id: ', ONESIGNAL_ID);

  if (!window?.plugins?.OneSignal) {
    console.warn('OneSignal Service failed to start. OneSignal plugin is not available.');
    return {
      notificationOpens$: Observable.of(),
      notificationReceives$: Observable.of(),
      notificationOpensData$: Observable.of(),
      notificationReceivesData$: Observable.of(),
      requestPushPermissions: () => {},
    };
  }

  window.plugins.OneSignal.initialize(ONESIGNAL_ID);

  const notificationOpensSubject = new Subject<OneSignalNotificationEvent>();
  const notificationReceiveSubject = new Subject<OneSignalNotificationEvent>();
  
  window.plugins.OneSignal.Notifications.addEventListener("click", (notificationReceivedEvent) => {
    notificationOpensSubject.next(notificationReceivedEvent);
  });

  window.plugins.OneSignal.Notifications.addEventListener("foregroundWillDisplay", (m: OneSignalNotificationEvent) => {
    notificationReceiveSubject.next(m);
    m.complete(m.notification);
  });

  useAuthStore.subscribe((state) => state.user, (user) => {
    if (user?.id) {
      console.log('[onesignal] account id', user.id);
      window.plugins.OneSignal.login(user.id);
      window.plugins.OneSignal.User.addTag("accountId", user.id);
    } else {
      console.log('[onesignal] no account id');
    }
  });

  const notificationOpens$ = notificationOpensSubject.share();
  const notificationReceives$ = notificationReceiveSubject.share();

  notificationOpens$
    .filter(m => !!m.notification)
    .do(m => console.log('notificationOpens', JSON.stringify(m.notification.additionalData)))
    .map(({ notification }) => notification.additionalData)
    .subscribe(m => console.log('notificationOpens$', m));

  notificationReceives$
    .filter(m => !!m.notification)
    .do(m => console.log('notificationReceives', JSON.stringify(m.notification.additionalData)))
    .map(({ notification }) => notification.additionalData)
    .subscribe(m => console.log('notificationReceives$', m));

  const notificationOpensData$ = notificationOpens$
    .filter(m => !!m.notification)
    .filter(({ notification }) => !!notification.additionalData)
    .map(({ notification }) => notification.additionalData as PushMessage);

  const notificationReceivesData$ = notificationReceives$
    .filter(m => !!m.notification)
    .filter(({ notification }) => !!notification.additionalData)
    .map(({ notification }) => notification.additionalData as PushMessage);

  const requestPushPermissions = (autoContinueToSettings = false) => {
    window.plugins.OneSignal.Notifications.requestPermission(true).then((accepted: boolean) => {
      Analytics.track('App', 'OneSignal Push permissions', accepted);
      console.log("User accepted notifications: " + accepted);
    });

    if (autoContinueToSettings) {
      if (!window?.plugins?.OneSignal?.Notifications?.hasPermission?.()) {
        Analytics.track('App', 'Open Notification settings');
        window?.cordova?.plugins?.diagnostic?.switchToSettings?.();
      }
    }
  };
  return {
    notificationOpens$,
    notificationReceives$,
    requestPushPermissions,
    notificationOpensData$,
    notificationReceivesData$
  };
}
